/* eslint-disable no-console */
import { Attributes } from '@leverege/ui-attributes';
import { Toast } from '@leverege/ui-elements';
import { Interface, Constants } from '@leverege/api-redux';
import { GlobalState } from '@leverege/ui-redux';
import { Config } from '@leverege/plugin';
import Molten from '@leverege/molten';
import { Relationship, GroupDelegate } from '@leverege/molten/imagine';
import FileSaver from 'file-saver';
import P from '@leverege/promise';
import * as Sentry from '@sentry/react';
import { Blueprints } from '@leverege/api-attributes';
export function getPathedAPI(deviceIds, api) {
  const systemId = Molten.getConfig('api/systemId');
  const {
    companyId,
    locationId,
    vehicleId,
    locatorId,
    saleId,
    billId,
    companyContractId
  } = deviceIds;
  let pathedApi;
  const generateApi = (resourceName, resourceId, pathedApi) => {
    let apiToReturn;
    if (pathedApi) {
      // Don't need to account for "companies",
      // because they're always top level
      apiToReturn = pathedApi.grp(`${resourceName}s`);
    } else {
      apiToReturn = api.interface(systemId, resourceName);
    }
    return apiToReturn.obj(resourceId);
  };
  if (companyId) {
    pathedApi = generateApi('company', companyId, pathedApi);
  }
  if (locationId) {
    pathedApi = generateApi('location', locationId, pathedApi);
  }
  if (vehicleId) {
    pathedApi = generateApi('vehicle', vehicleId, pathedApi);
  } else if (locatorId) {
    pathedApi = generateApi('locator', locatorId, pathedApi);
  } else if (saleId) {
    pathedApi = generateApi('sale', saleId, pathedApi);
  } else if (billId) {
    pathedApi = generateApi('bill', billId, pathedApi);
  } else if (companyContractId) {
    pathedApi = generateApi('companyContract', companyContractId, pathedApi);
  }
  return pathedApi;
}
export async function getLocatorLocationAndCompany(locatorId, options) {
  const {
    actions,
    location
  } = options;
  const companyId = Attributes.get('location.company', 'location', location.data);
  const locationId = location.id;
  let company, locator, loc;
  if (companyId) {
    const relationship = Relationship.getByPath('company').createResolved(actions.match);
    const nActions = new GroupDelegate({
      relationship,
      match: actions.match,
      queryName: 'pairing',
      ttl: 30000
    });
    const res = await P.pipeline({
      company: () => {
        return GlobalState.dispatch(nActions.item(companyId).get());
      },
      locator: () => {
        return GlobalState.dispatch(nActions.item(companyId).child('locators').item(locatorId).get());
      },
      location: ['locator', async locator => {
        if (!locator.data?.location?.id) {
          return null;
        }
        return GlobalState.dispatch(nActions.item(companyId).child('locations').item(locator.data.location.id).get());
      }]
    });
    locator = res.locator;
    company = res.company;
    loc = res.location;
  } else {
    locator = await GlobalState.dispatch(Interface.create(actions.systemId, 'location').obj(locationId).grp('locators').obj(locatorId).get());
  }
  return {
    location: loc || location.data,
    locator,
    company
  };
}
export async function getVehicleLocationAndCompany(vehicleId, options) {
  const {
    actions,
    location
  } = options;
  const companyId = Attributes.get('location.company', 'location', location.data);
  let company, vehicle, loc;
  if (companyId) {
    const relationship = Relationship.getByPath('company').createResolved(actions.match);
    const nActions = new GroupDelegate({
      relationship,
      match: actions.match,
      queryName: 'pairing',
      ttl: 30000
    });
    const res = await P.pipeline({
      company: () => {
        return GlobalState.dispatch(nActions.item(companyId).get());
      },
      vehicle: () => {
        return GlobalState.dispatch(nActions.item(companyId).child('vehicles').item(vehicleId).get());
      },
      location: ['vehicle', async vehicle => {
        if (!vehicle?.data?.location?.id) {
          return null;
        }
        return GlobalState.dispatch(nActions.item(companyId).child('locations').item(vehicle.data.location.id).get());
      }]
    });
    vehicle = res.vehicle;
    company = res.company;
    loc = res.location;
  } else {
    const relationship = Relationship.getByPath('location').createResolved(actions.match);
    const nActions = new GroupDelegate({
      relationship,
      match: actions.match,
      queryName: 'pairing',
      ttl: 30000
    });
    vehicle = await GlobalState.dispatch(nActions.item(location.id).child('vehicles').item(vehicleId).get());
  }
  return {
    location: loc || location.data,
    vehicle,
    company
  };
}
export async function pairLocatorToVehicle(opts) {
  const {
    locatorId,
    vehicleId,
    companyId,
    locationId,
    installLocation,
    force,
    saleId
  } = opts;
  const systemId = Molten.getConfig('api/systemId');
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    let nApi;
    if (locationId && companyId) {
      nApi = api.interface(systemId, 'company').obj(companyId).grp('locations').obj(locationId).grp('locators');
    } else if (locationId) {
      nApi = api.interface(systemId, 'location').obj(locationId).grp('locators');
    } else if (companyId) {
      nApi = api.interface(systemId, 'company').obj(companyId).grp('locators');
    } else {
      nApi = api.interface(systemId, 'locator');
    }
    return nApi.obj(locatorId).srv('recovr').post('pairToVehicle', {
      vehicleId,
      installLocation,
      force,
      saleId
    });
  });
}
export async function unpairLocatorFromVehicle({
  locatorId,
  companyId,
  locationId,
  unpairReason,
  notes
}) {
  const systemId = Molten.getConfig('api/systemId');
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    let nApi;
    if (locationId && companyId) {
      nApi = api.interface(systemId, 'company').obj(companyId).grp('locations').obj(locationId).grp('locators');
    } else if (locationId) {
      nApi = api.interface(systemId, 'location').obj(locationId).grp('locators');
    } else if (companyId) {
      nApi = api.interface(systemId, 'company').obj(companyId).grp('locators');
    } else {
      nApi = api.interface(systemId, 'locator');
    }
    return nApi.obj(locatorId).srv('recovr').post('unpairFromVehicle', {
      unpairReason,
      notes
    });
  });
}
export function resetLocator(systemId, locatorId) {
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    return api.interface(systemId, 'locator').obj(locatorId).srv('recovr').post('requestReset').catch(() => {
      Toast.error('Error resetting locator');
    });
  });
}
export function cancelLocatorReset(systemId, locatorId) {
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    return api.interface(systemId, 'locator').obj(locatorId).srv('recovr').post('cancelResetRequest').catch(() => {
      Toast.error('Error cancelling locator reset');
    });
  });
}
export async function getLocations(systemId, companyId, opts) {
  const action = Interface.create(systemId, 'company').obj(companyId).grp('locations').list();
  let {
    items
  } = await GlobalState.dispatch(action);
  items = items.map(item => {
    return {
      ...item,
      companyName: opts?.companyName
    };
  });
  return items;
}
export async function getExistingContract(systemId, companyId, startDate, endDate, queryName = 'existingContract') {
  const action = Interface.create(systemId, 'company').obj(companyId).grp('companyContracts').search({
    filter: {
      type: 'logical',
      operator: 'or',
      conditions: [{
        type: 'logical',
        operator: 'or',
        conditions: [{
          type: 'comparison',
          field: 'data/contractStartDate',
          value1: startDate,
          value2: endDate,
          comparator: '()',
          isDate: true
        }, {
          type: 'comparison',
          field: 'data/contractEndDate',
          value1: startDate,
          value2: endDate,
          comparator: '()',
          isDate: true
        }]
      }, {
        type: 'logical',
        operator: 'and',
        conditions: [{
          type: 'comparison',
          field: 'data/contractStartDate',
          comparator: '<=',
          value: startDate,
          isDate: true
        }, {
          type: 'comparison',
          field: 'data/contractEndDate',
          comparator: '>=',
          value: endDate,
          isDate: true
        }]
      }]
    }
  }, undefined, queryName);
  const {
    items
  } = await GlobalState.dispatch(action);
  return items;
}
export async function getCurrentMonthlyBilling(systemId, companyId, locationId) {
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    const billingModule = Config.get('RecovrServer', 'billingModule');
    return api.interface(systemId, 'company').obj(companyId).srv(billingModule).get('currentMonthlyBilling', {
      locationId
    });
  });
}
export function getInvoiceResource(systemId, companyId, locationId, billingId, resourceName) {
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    let nApi;
    if (locationId) {
      if (companyId) {
        nApi = api.interface(systemId, 'company').obj(companyId).grp('locations').obj(locationId).grp('bills').obj(billingId).resource(resourceName);
      } else {
        nApi = api.interface(systemId, 'location').obj(locationId).grp('bills').obj(billingId).resource(resourceName);
      }
    } else {
      nApi = api.interface(systemId, 'company').obj(companyId).grp('bills').obj(billingId).resource(resourceName);
    }
    return nApi.get().then(resource => {
      const blob = generateFileBlob(resource.data, resourceName);
      FileSaver.saveAs(blob, resource.data.filename);
    });
  });
}
function generateFileBlob(resourceData, resourceName) {
  if (resourceName === 'reportsSpreadsheet') {
    return new Blob([Buffer.from(resourceData.data.data)], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  }
  return new Blob([resourceData.data], {
    type: 'text/csv'
  });
}
export function getInvoicePDF(systemId, companyId, locationId, billingId, filename) {
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    const billingModule = Config.get('RecovrServer', 'billingModule');
    let nApi;
    if (locationId) {
      if (companyId) {
        nApi = api.interface(systemId, 'company').obj(companyId).grp('locations').obj(locationId);
      } else {
        nApi = api.interface(systemId, 'location').obj(locationId);
      }
    } else {
      nApi = api.interface(systemId, 'company').obj(companyId);
    }
    nApi = nApi.grp('bills').obj(billingId).srv(billingModule);
    return nApi.get('invoicePDF').then(response => response.blob()).then(blob => {
      FileSaver.saveAs(blob, filename);
    });
  });
}
export function getAddendumPDF(systemId, companyId, locationId, billingId, filename) {
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    const billingModule = Config.get('RecovrServer', 'billingModule');
    let nApi;
    if (locationId) {
      if (companyId) {
        nApi = api.interface(systemId, 'company').obj(companyId).grp('locations').obj(locationId);
      } else {
        nApi = api.interface(systemId, 'location').obj(locationId);
      }
    } else {
      nApi = api.interface(systemId, 'company').obj(companyId);
    }
    nApi = nApi.grp('bills').obj(billingId).srv(billingModule);
    return nApi.get('addendumPDF').then(response => response.blob()).then(blob => {
      FileSaver.saveAs(blob, filename);
    });
  });
}
export function getContractPDF({
  systemId,
  companyId,
  locationId,
  saleId,
  filename,
  productType = 'recovr'
}) {
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    let nApi;
    if (locationId) {
      if (companyId) {
        nApi = api.interface(systemId, 'company').obj(companyId).grp('locations').obj(locationId);
      } else {
        nApi = api.interface(systemId, 'location').obj(locationId);
      }
    } else {
      nApi = api.interface(systemId, 'company').obj(companyId);
    }
    nApi = nApi.grp('sales').obj(saleId).srv('contracts');
    return nApi.get(`/location/${locationId}/sale/${saleId}/contractPDF`, {
      productType
    }).then(response => response.blob()).then(blob => {
      FileSaver.saveAs(blob, filename);
    });
  });
}
export async function getActiveContractsByCompanyId(systemId, companyIds) {
  const {
    items
  } = await GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    const now = Date.now();
    return api.interface(systemId, 'companyContract').search({
      filter: {
        type: 'logical',
        operator: 'and',
        conditions: [{
          type: 'comparison',
          field: 'data/contractStartDate',
          value: now,
          comparator: '<=',
          isDate: true
        }, {
          type: 'comparison',
          field: 'data/contractEndDate',
          value: now,
          comparator: '>=',
          isDate: true
        }, {
          type: 'equals',
          field: 'data/company/id',
          value: companyIds
        }]
      }
    });
  });
  return items.reduce((acc, contract) => ({
    ...acc,
    [contract.data.company.id]: contract
  }), {});
}
export function getSale(systemId, companyId, locationId, saleId) {
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    const locationsGroup = companyId ? api.interface(systemId, 'company').obj(companyId).grp('locations') : api.interface(systemId, 'location');
    return locationsGroup.obj(locationId).grp('sales').obj(saleId).get();
  });
}
export function updateSimOTAActive(systemId, value) {
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    return api.system(systemId).srv('recovr').put('updateSimOTAActive', {
      value
    }).catch(() => {
      Toast.error('Error updating system SIM OTA active status');
    });
  });
}
export function updateSimOTAEnabledForAllLocations(systemId, value) {
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    return api.system(systemId).srv('recovr').put('updateSimOTAEnabledAllLocations', {
      value
    }).catch(() => {
      Toast.error('Error updating system SIM OTA global enable status');
    });
  });
}
export async function pairKeysToVehicle(options) {
  const {
    companyId,
    locationId,
    vehicleId,
    keyIds,
    force
  } = options;
  try {
    const pairSuccesses = [];
    const pairErrors = [];
    await GlobalState.dispatch(async (dispatch, getState, {
      api
    }) => {
      // wait for all keys to pair in parallel
      // keyId refers to the key device's unique id, not the attribute keyId
      await P.mapLimit(keyIds, async keyId => {
        let nApi;
        try {
          if (companyId && locationId) {
            nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('vehicles');
          } else if (locationId) {
            nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('vehicles');
          }
          const res = await nApi.obj(`${vehicleId}`).srv('r4kServer').post('pairKeysToVehicle', {
            keyId,
            force
          });
          if (res && !res?.error && (!res.status || res.status === 200 || res.status === 201)) {
            pairSuccesses.push(res);
          } else {
            pairErrors.push(res);
            Toast.error(`Could not pair key ${keyId} to ${vehicleId}`);
          }
        } catch (err) {
          Sentry.captureException(err);
          console.error(`Error pairing key ${keyId} to vehicle ${vehicleId}: ${err}`);
        }
      }, {
        limit: 5
      });
      // Consolidate all successes into a single toast
      const pairSuccessesLength = pairSuccesses.length;
      const pairErrorsLength = pairErrors.length;
      const pairTotal = pairSuccessesLength + pairErrorsLength;
      if (pairSuccessesLength === 1 && pairErrorsLength === 0) {
        Toast.success('Successfully paired key to vehicle');
      } else if (pairSuccessesLength > 0) {
        Toast.success(`Successfully paired ${pairSuccessesLength} out of 
          ${pairTotal} keys to vehicle ${vehicleId}`);
      }
    });
  } catch (err) {
    console.error(`Could not pair keys to vehicle: ${err}`);
    Sentry.captureException(err);
    throw err;
  }
  return false;
}
export async function unpairKeysFromVehicle(options) {
  const {
    companyId,
    locationId,
    vehicleId,
    keyIds
  } = options;
  try {
    const unpairSuccesses = [];
    const unpairErrors = [];
    await GlobalState.dispatch(async (dispatch, getState, {
      api
    }) => {
      // wait for all keys to unpair in parallel
      // keyId refers to the key device's unique id, not the attribute keyId
      await P.mapLimit(keyIds, async keyId => {
        let nApi;
        try {
          if (companyId && locationId) {
            nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('vehicles');
          } else if (locationId) {
            nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('vehicles');
          }
          const res = await nApi.obj(`${vehicleId}`).srv('r4kServer').post('unpairKeysFromVehicle', {
            keyId
          });
          if (res && !res?.error && (!res.status || res.status === 200 || res.status === 201)) {
            unpairSuccesses.push(res);
          } else {
            unpairErrors.push(res);
            Toast.error(`Could not unpair key ${keyId} from ${vehicleId}`);
          }
        } catch (err) {
          console.error(`Error unpairing key ${keyId} from vehicle ${vehicleId}: ${err}`);
          Sentry.captureException(err);
        }
      }, {
        limit: 5
      });
      // Consolidate all successes into a single toast
      const unpairSuccessesLength = unpairSuccesses.length;
      const unpairErrorsLength = unpairErrors.length;
      const pairTotal = unpairSuccessesLength + unpairErrorsLength;
      if (unpairSuccessesLength === 1 && unpairErrorsLength === 0) {
        Toast.success('Successfully unpaired key from vehicle');
      } else if (unpairSuccessesLength > 0) {
        Toast.success(`Successfully unpaired ${unpairSuccessesLength} out of 
          ${pairTotal} keys from vehicle ${vehicleId}`);
      }
    });
  } catch (err) {
    console.error(`Could not unpair keys from vehicle: ${err}`);
    Sentry.captureException(err);
    throw err;
  }
  return false;
}

// checks for permission to make pairKeysToVehicle request to show action
export async function canPairKeysToVehicle(options) {
  const {
    companyId,
    locationId,
    vehicleId
  } = options;
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    let nApi;
    if (companyId && locationId) {
      nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('vehicles');
    } else if (locationId) {
      nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('vehicles');
    }
    const res = await nApi.obj(`${vehicleId}`).srv('r4kServer').post('pairKeysToVehicle', null, {
      test: true
    });
    dispatch({
      type: Constants.ALLOWED_SET,
      resourceId: vehicleId,
      action: 'pairKeysToVehicle',
      allowed: res.allowed === true
    });
    return res.allowed;
  });
}

// checks for permission to delete a vehicle from a location
export async function canRemoveVehicle(options) {
  const {
    companyId,
    locationId,
    vehicleId
  } = options;
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    const nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('vehicles').obj(vehicleId).delete({
      test: true
    });
    const res = await nApi;
    return res;
  });
}

// returns number of tags paired to a vehicle
export async function getTagCountOfVehicle(options) {
  const {
    companyId,
    locationId,
    vehicleId
  } = options;
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    let nApi;
    try {
      if (companyId && locationId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('vehicles');
      } else if (locationId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('vehicles');
      }
      const res = await nApi.obj(`${vehicleId}`).srv('r4kServer').get('getTagCountOfVehicle');
      const total = res?.total || 0;
      return total;
    } catch (err) {
      console.log('Error getting tag count of vehicle', {
        err
      });
      return -1;
    }
  });
}
export async function canTransferVehicleToSiblingLocation(options) {
  const {
    locationId,
    companyId
  } = options;
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    try {
      let nApi;
      if (companyId && locationId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).srv('recovr').get('transferVehicle', {
          test: true
        });
      } else {
        nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).srv('recovr').get('transferVehicle', {
          test: true
        });
      }
      const res = await nApi;
      return res.allowed;
    } catch (err) {
      console.error({
        err
      }, 'Error checking transfer vehicle permission');
      return false;
    }
  });
}
export async function canGetAllSystemUsers(profileId) {
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    const res = await api.system(process.env.SYSTEM_ID).srv('recovr').get('searchUsers', {
      test: true
    });
    dispatch({
      type: Constants.ALLOWED_SET,
      resourceId: profileId,
      action: 'searchUsers',
      allowed: res.allowed === true
    });
    return res.allowed;
  });
}
export async function getAllSystemUsers(perPage, page = 1) {
  const start = (page - 1) * perPage;
  let systemUsers;
  try {
    systemUsers = await GlobalState.dispatch(async (dispatch, getState, {
      api
    }) => {
      return api.system(process.env.SYSTEM_ID).srv('recovr').get(`searchUsers?perPage=${perPage}&start=${start}`);
    });
  } catch (err) {
    console.error({
      err
    }, 'Error getting list of searchUsers');
  }
  return systemUsers;
}
export async function getUserByEmail(email) {
  let users;
  try {
    users = await GlobalState.dispatch(async (dispatch, getState, {
      api
    }) => {
      return api.system(process.env.SYSTEM_ID).srv('recovr').get(`searchUsers/getUserByEmail?email=${email}`);
    });
  } catch (err) {
    console.error({
      err
    }, 'Could not get user by email');
  }
  return users;
}

// list devices under a user
export async function getDevicesUnderUser(userId) {
  let devices;
  try {
    devices = await GlobalState.dispatch(async (dispatch, getState, {
      api
    }) => {
      return api.system(process.env.SYSTEM_ID).srv('recovr').get(`searchUsers/getDevicesUnderUser?userId=${userId}`);
    });
  } catch (err) {
    console.error({
      err
    }, 'Could not get devices under user');
  }
  return devices;
}
export async function getRolesOnDeviceFromUser(userId) {
  let devices;
  try {
    devices = await GlobalState.dispatch(async (dispatch, getState, {
      api
    }) => {
      return api.system(process.env.SYSTEM_ID).srv('recovr').get(`searchUsers/getRolesOnDeviceFromUser?userId=${userId}`);
    });
  } catch (err) {
    console.error({
      err
    }, 'Could not get roles on device from user');
  }
  return devices;
}
export async function getUserName(userId) {
  let item;
  try {
    item = await GlobalState.dispatch(async (dispatch, getState, {
      api
    }) => {
      return api.system(process.env.SYSTEM_ID).srv('recovr').get(`searchUsers/getUserName?userId=${userId}`);
    });
  } catch (err) {
    console.error({
      err
    }, 'Could not get users name');
  }
  return item;
}
export async function getConsolidatedDeviceData(userId) {
  try {
    const devices = await getDevicesUnderUser(userId);
    const rolesData = await getRolesOnDeviceFromUser(userId);
    const transformedData = await Promise.all(devices.map(async device => {
      const blueprintData = await Blueprints.getByType(device.blueprintId);
      const blueprintType = blueprintData.type || 'Unknown';
      const deviceRoles = rolesData.filter(role => role.resourceId === device.id);
      const rolesList = deviceRoles.length > 0 ? deviceRoles.map(role => role?.role.displayName || role?.role.name) : ['No role found'];
      return {
        ...device,
        blueprintType,
        roles: rolesList,
        created: new Date(device.created).toLocaleString(),
        lastModified: new Date(device.lastModified).toLocaleString()
      };
    }));
    return transformedData;
  } catch (error) {
    console.error('Error fetching consolidated device data', error);
    throw error;
  }
}